/**
 * Living Vision — Setup Flow (entrance only).
 *
 * Strict scope: this is the *guided entrance* to the existing Living
 * Vision experience. It does NOT modify, re-create, or replace any of
 * the existing /abundance/living-vision-board engine.
 *
 *   Step 1  Explanation       — Living Vision · See it. Feel it. Become it.
 *   Step 2  Focus Selection   — pick 2–3 categories, then pick at least
 *                                3 images per category (heart-favourite
 *                                UI mirroring the existing experience).
 *   Step 3  Word Confirmation — three pre-written identity lines per
 *                                category, removable, no typing.
 *   Step 4  Enter Living      — 0.7s fade-to-black + soft amber pulse,
 *                                then navigate into the existing engine.
 *
 * Persistence: localStorage["reclaim_perf_vision_v1"] stores
 *   { focuses: string[], images: { focus: url[] }, identity: { focus: line[] } }.
 *
 * Visuals: same EDGE dark amber world. Cormorant serif titles, glow
 * CTA, "Keep it simple." anchor under every CTA.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Heart, X } from "lucide-react";

const STORE_KEY = "reclaim_perf_vision_v1";
const API_URL   = process.env.REACT_APP_BACKEND_URL;

// ── Bridge to existing engine ──────────────────────────────────────
// Pre-favourite every selected image inside the SAME
// `vision_board_favorites` collection the LivingVisionBoard engine
// already reads on mount (see /app/frontend/src/pages/Abundance/
// LivingVisionBoard.jsx line ~2893). When the user lands inside the
// engine, their hearts are already lit — the canvas is seeded.
async function bridgeFavouritesToEngine({ images, focusEngineMap }) {
  const userId = (() => {
    let u = localStorage.getItem("userId");
    if (u) return u;
    u = "user_" + Math.random().toString(36).slice(2, 11);
    localStorage.setItem("userId", u);
    return u;
  })();
  const url = `${API_URL}/api/vision-board/favorites/toggle`;
  const calls = [];
  for (const [focusId, urls] of Object.entries(images || {})) {
    const category = focusEngineMap[focusId] || "performance";
    for (const image_url of urls) {
      calls.push(
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, image_url, category }),
        }).catch(() => null)
      );
    }
  }
  await Promise.allSettled(calls);
}

// ── Five focus categories ──────────────────────────────────────────
// `engineCategory` maps each focus to the existing LivingVisionBoard
// category ID so the bridge can pre-favourite the user's images
// against the same `vision_board_favorites` collection the engine
// already consumes.
const FOCUS_OPTIONS = [
  { id: "leadership", label: "Leadership",            blurb: "People follow your energy",   engineCategory: "performance" },
  { id: "team",       label: "Team Growth",           blurb: "Your organisation expanding", engineCategory: "performance" },
  { id: "income",     label: "Income & Wealth",       blurb: "Money flowing through you",   engineCategory: "wealth" },
  { id: "presence",   label: "Confidence & Presence", blurb: "Calm, grounded power",        engineCategory: "self" },
  { id: "freedom",    label: "Freedom Lifestyle",     blurb: "Time, space, choice",         engineCategory: "freedom" },
  { id: "luxury",     label: "Luxury & Lifestyle",    blurb: "Watches, drives, fine living", engineCategory: "luxury" },
];

// ── Curated images per focus (no luxury clichés). 10–12 each. ─────
const FOCUS_IMAGES = {
  leadership: [
    "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.unsplash.com/photo-1758600587839-56ba05596c69?auto=format&fit=crop&w=900",
    "https://images.unsplash.com/photo-1758598497635-48cbbb1f6555?auto=format&fit=crop&w=900",
    "https://images.pexels.com/photos/2422290/pexels-photo-2422290.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/2774556/pexels-photo-2774556.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3184405/pexels-photo-3184405.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3184432/pexels-photo-3184432.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/2422294/pexels-photo-2422294.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
  ],
  team: [
    "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3182763/pexels-photo-3182763.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.unsplash.com/photo-1758691737138-7b9b1884b1db?auto=format&fit=crop&w=900",
    "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3184394/pexels-photo-3184394.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/7793691/pexels-photo-7793691.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/1181605/pexels-photo-1181605.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3184398/pexels-photo-3184398.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
  ],
  income: [
    "https://images.pexels.com/photos/4386366/pexels-photo-4386366.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/259200/pexels-photo-259200.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3943723/pexels-photo-3943723.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/4386478/pexels-photo-4386478.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/6772076/pexels-photo-6772076.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/53621/calculator-calculation-insurance-finance-53621.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/7567228/pexels-photo-7567228.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/164527/pexels-photo-164527.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/259027/pexels-photo-259027.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/210600/pexels-photo-210600.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
  ],
  presence: [
    "https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/1547248/pexels-photo-1547248.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/1820770/pexels-photo-1820770.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/2613260/pexels-photo-2613260.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/2078265/pexels-photo-2078265.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3812743/pexels-photo-3812743.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/1535244/pexels-photo-1535244.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/3214671/pexels-photo-3214671.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/2387873/pexels-photo-2387873.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
  ],
  freedom: [
    "https://images.pexels.com/photos/2161467/pexels-photo-2161467.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/289586/pexels-photo-289586.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/2387873/pexels-photo-2387873.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/268533/pexels-photo-268533.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/1287145/pexels-photo-1287145.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/355465/pexels-photo-355465.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/220836/pexels-photo-220836.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/1287145/pexels-photo-1287145.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/2086361/pexels-photo-2086361.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/302804/pexels-photo-302804.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
  ],
  luxury: [
    // Aspirational lifestyle — refined, not flashy. Watches, well-cut
    // suits, supercars on coastal roads, fine dining, mega yachts.
    "https://images.pexels.com/photos/15564127/pexels-photo-15564127.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/17597363/pexels-photo-17597363.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/26563761/pexels-photo-26563761.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/12715491/pexels-photo-12715491.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/20562279/pexels-photo-20562279.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/6700040/pexels-photo-6700040.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/31977873/pexels-photo-31977873.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/17635350/pexels-photo-17635350.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/12488707/pexels-photo-12488707.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
    "https://images.pexels.com/photos/20648955/pexels-photo-20648955.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=900",
  ],
};

// ── Identity lines per focus (3 each, pre-written; review only) ────
const FOCUS_IDENTITY = {
  leadership: ["I lead with clarity", "People follow my energy", "I am trusted"],
  team:       ["My team is growing daily", "New people are stepping up", "Leaders are emerging around me"],
  income:     ["Opportunities are flowing", "Money moves through me", "Abundance feels normal"],
  presence:   ["I stay grounded", "My presence does the work", "Calm power"],
  freedom:    ["I move on my own time", "Space is opening", "Life is light"],
  luxury:     ["I live with quality", "Beautiful things come to me", "I enjoy the rewards"],
};

const MIN_IMAGES_PER_CATEGORY = 3;

// ── Persistence ────────────────────────────────────────────────────
function loadSaved() {
  try { const raw = localStorage.getItem(STORE_KEY); return raw ? JSON.parse(raw) : null; }
  catch { return null; }
}
function saveVision(payload) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify({ ...payload, savedAt: Date.now() })); }
  catch (_) {}
}

export default function PerformanceVisionSetup() {
  const navigate = useNavigate();
  const saved = useMemo(loadSaved, []);

  const [step, setStep] = useState(1);                // 1..4
  const [step2Phase, setStep2Phase] = useState("pick"); // 'pick' | 'images'
  const [focuses, setFocuses] = useState(saved?.focuses || []);
  const [images,  setImages]  = useState(saved?.images  || {}); // { focus: url[] }
  const [identity, setIdentity] = useState(() => {
    if (saved?.identity) return saved.identity;
    const m = {};
    for (const f of FOCUS_OPTIONS) m[f.id] = [...FOCUS_IDENTITY[f.id]];
    return m;
  });
  const [activeFocusIdx, setActiveFocusIdx] = useState(0); // for Step 2b
  const [exiting, setExiting] = useState(false);            // Step 4 fade

  // Live-fetched per-category image pools. Falls back to baked
  // FOCUS_IMAGES if the API request fails. Lets the admin update
  // images via /admin/networker-vision-images without a code deploy.
  const [livePools, setLivePools] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/admin/networker-vision/images`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        if (cancelled) return;
        const pools = {};
        for (const [cat, payload] of Object.entries(data?.categories || {})) {
          const urls = (payload?.urls || []).filter(Boolean);
          if (urls.length) pools[cat] = urls;
        }
        if (Object.keys(pools).length) setLivePools(pools);
      })
      .catch(() => {
        // Silent — wizard still works with baked defaults.
      });
    return () => { cancelled = true; };
  }, []);

  const imagePool = (focusId) => (livePools?.[focusId] || FOCUS_IMAGES[focusId] || []);

  const toggleFocus = (id) => {
    setFocuses((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((x) => x !== id);
        // also drop their image picks for cleanliness
        setImages((im) => { const c = { ...im }; delete c[id]; return c; });
        return next;
      }
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const toggleImage = (focusId, url) => {
    setImages((prev) => {
      const cur = prev[focusId] || [];
      const has = cur.includes(url);
      return { ...prev, [focusId]: has ? cur.filter((u) => u !== url) : [...cur, url] };
    });
  };

  const removeIdentityLine = (focusId, line) => {
    setIdentity((prev) => ({ ...prev, [focusId]: (prev[focusId] || []).filter((l) => l !== line) }));
  };

  // Step 4 — fade-to-black then navigate STRAIGHT INTO the immersion.
  // Bug fix: previously we navigated to /abundance/living-vision-board with
  // no state, landing the user at Step 0 (creation entry) and forcing them
  // to click through 6 screens to reach their immersion. Now we pass
  // `savedImmersion` + `startAtStep: 6` so they drop directly into the
  // cinematic playback with their picks + identity lines prefilled.
  const handleEnterLiving = async () => {
    saveVision({ focuses, images, identity });
    setExiting(true);

    // Build focus → engine category map fresh in case user removed a focus.
    const focusEngineMap = {};
    for (const f of FOCUS_OPTIONS) focusEngineMap[f.id] = f.engineCategory;

    // Flatten wizard data into engine-shaped `savedImmersion` payload.
    const flatSelectedImages = [];
    for (const [focusId, urls] of Object.entries(images || {})) {
      const cat = focusEngineMap[focusId] || focusId;
      for (const url of urls || []) flatSelectedImages.push({ url, category: cat });
    }
    const flatAffirmations = [];
    for (const fid of focuses || []) {
      for (const line of identity?.[fid] || []) flatAffirmations.push(line);
    }

    const navState = {
      savedImmersion: {
        selected_images: flatSelectedImages,
        selected_words: [],
        selected_affirmations: flatAffirmations,
      },
      startAtStep: 6, // jump straight to the still-image immersion screen
    };

    // Pre-seed favourites on backend (non-blocking) for re-entry.
    bridgeFavouritesToEngine({ images, focusEngineMap }).finally(() => {
      setTimeout(
        () => navigate("/abundance/living-vision-board", { state: navState }),
        350,
      );
    });
    // Hard cap: even if bridge hangs, navigate after 1100ms total.
    setTimeout(() => {
      if (window.location.pathname.startsWith("/networker/performance-vision")) {
        navigate("/abundance/living-vision-board", { state: navState });
      }
    }, 1100);
  };

  const totalSteps = 4;
  const progress   = step / totalSteps;
  const stepLabel  = ["", "Living Vision", "Focus", "Identity", "Entering"][step];

  // Step 2 sub-state.
  const inImagePickerMode = step === 2 && step2Phase === "images" && focuses.length >= 2;

  const handleStep2Continue = () => {
    if (focuses.length < 2) return;
    if (step2Phase === "pick") {
      setActiveFocusIdx(0);
      setStep2Phase("images");
      return;
    }
    // In image-picker phase — advance to next category or step 3.
    if (activeFocusIdx < focuses.length - 1) {
      setActiveFocusIdx(activeFocusIdx + 1);
    } else {
      setStep(3);
    }
  };

  // Top-bar back button behaviour
  const handleBack = () => {
    if (step === 2 && step2Phase === "images") {
      if (activeFocusIdx > 0) setActiveFocusIdx(activeFocusIdx - 1);
      else setStep2Phase("pick");
      return;
    }
    if (step > 1) { setStep(step - 1); return; }
    navigate("/networker");
  };

  return (
    <div
      className="fixed inset-0 overflow-hidden"
      data-testid="living-vision-setup"
      style={{
        background: "radial-gradient(ellipse at 50% 28%, #1A1410 0%, #0E0B08 55%, #050402 100%)",
      }}
    >
      {/* Slow ambient drift */}
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(circle at 26% 20%, rgba(217,165,90,0.10) 0%, transparent 60%)",
            "radial-gradient(circle at 74% 32%, rgba(217,165,90,0.10) 0%, transparent 60%)",
            "radial-gradient(circle at 26% 20%, rgba(217,165,90,0.10) 0%, transparent 60%)",
          ],
        }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Top bar */}
      {!exiting && (
        <>
          <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-5 py-4">
            <button
              onClick={handleBack}
              data-testid="lv-setup-back-btn"
              className="text-white/45 hover:text-white/90 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-[10px] uppercase tracking-[0.4em]">{step === 1 ? "The Edge" : stepLabel}</span>
            </button>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/30">
                {step} / {totalSteps}
              </span>
              <button
                onClick={() => navigate("/networker")}
                data-testid="lv-setup-close-btn"
                className="text-white/45 hover:text-white/90"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="absolute top-12 inset-x-0 h-px bg-white/[0.06] z-10">
            <motion.div
              className="h-full"
              style={{ background: "linear-gradient(90deg, transparent, rgba(217,165,90,0.65), transparent)" }}
              animate={{ width: `${progress * 100}%` }}
              transition={{ duration: 0.55 }}
            />
          </div>
        </>
      )}

      <AnimatePresence mode="wait">
        {step === 1 && !exiting && (
          <ExplanationStep key="1" onContinue={() => setStep(2)} />
        )}
        {step === 2 && !inImagePickerMode && !exiting && (
          <FocusPickStep
            key="2-cat"
            focuses={focuses}
            onToggle={toggleFocus}
            onContinue={handleStep2Continue}
          />
        )}
        {step === 2 && inImagePickerMode && !exiting && (
          <ImagePickerStep
            key={`2-img-${activeFocusIdx}`}
            focusId={focuses[activeFocusIdx]}
            focusLabel={FOCUS_OPTIONS.find((f) => f.id === focuses[activeFocusIdx])?.label}
            isLast={activeFocusIdx === focuses.length - 1}
            indexLabel={`${activeFocusIdx + 1} of ${focuses.length}`}
            selected={images[focuses[activeFocusIdx]] || []}
            pool={imagePool(focuses[activeFocusIdx])}
            onToggleImage={(url) => toggleImage(focuses[activeFocusIdx], url)}
            onContinue={handleStep2Continue}
          />
        )}
        {step === 3 && !exiting && (
          <IdentityReviewStep
            key="3"
            focuses={focuses}
            identity={identity}
            onRemoveLine={removeIdentityLine}
            onEnter={() => { setStep(4); setTimeout(handleEnterLiving, 150); }}
          />
        )}
        {(step === 4 || exiting) && (
          <TransitionStep key="4" />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── STEP 1 — Explanation ──────────────────────────────────────────
function ExplanationStep({ onContinue }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
      animate={{ opacity: 1, y: 0,  filter: "blur(0px)" }}
      exit={{    opacity: 0, y: -8, filter: "blur(4px)" }}
      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0 flex flex-col items-center justify-center px-7 pb-32 max-w-xl mx-auto text-center"
      data-testid="lv-setup-step-1"
    >
      <h1 className="text-white font-medium mb-2"
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: "clamp(38px, 6.8vw, 54px)",
            letterSpacing: "-0.012em",
            textShadow: "0 6px 36px rgba(217,165,90,0.22)",
          }}>
        Living Vision
      </h1>
      <p className="text-amber-200/85 text-[14px] uppercase tracking-[0.22em] mb-7" style={{ fontWeight: 500 }}>
        See it. Feel it. Become it.
      </p>
      <div className="space-y-3 max-w-[36ch] text-white/75"
           style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "16.5px", lineHeight: 1.55 }}>
        <p>We invite you to create your own vision.</p>
        <p className="text-white/60">
          Most of our thoughts and behaviours are shaped by the subconscious — patterns we've absorbed, not consciously chosen.
        </p>
        <p>This is your opportunity to change that.</p>
        <p className="text-white/60">
          Select a few areas that matter to you. Choose the images and words that reflect where you're going.
        </p>
        <p>Then step into it.</p>
        <p className="italic text-white/55">
          Not just something you see — something you begin to feel.
        </p>
      </div>
      <BottomCta onClick={onContinue} label="Create My Vision" testId="lv-setup-step-1-cta" />
    </motion.div>
  );
}

// ── STEP 2a — Focus Picker (categories) ───────────────────────────
function FocusPickStep({ focuses, onToggle, onContinue }) {
  const canAdvance = focuses.length >= 2;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      className="absolute inset-0 flex flex-col items-center justify-start px-6 pt-24 pb-32 max-w-xl mx-auto"
      data-testid="lv-setup-step-2-categories"
    >
      <h2 className="text-white font-medium text-center mb-2"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "30px", letterSpacing: "-0.012em" }}>
        What matters most right now?
      </h2>
      <p className="text-white/55 text-sm mb-6 text-center">Pick 2–3. We'll build from these.</p>

      <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {FOCUS_OPTIONS.map((f) => {
          const active = focuses.includes(f.id);
          return (
            <button
              key={f.id}
              onClick={() => onToggle(f.id)}
              data-testid={`lv-focus-${f.id}`}
              className="relative text-left rounded-xl px-4 py-3.5 transition-all duration-300"
              style={{
                background: active
                  ? "linear-gradient(135deg, rgba(217,165,90,0.22), rgba(217,165,90,0.06))"
                  : "rgba(15,11,8,0.72)",
                border: `1px solid ${active ? "rgba(217,165,90,0.55)" : "rgba(255,255,255,0.08)"}`,
                boxShadow: active ? "0 10px 26px -10px rgba(217,165,90,0.55)" : "none",
              }}
            >
              <h3 className="text-white text-[15px] font-semibold leading-tight">{f.label}</h3>
              <p className="text-white/45 text-xs mt-0.5">{f.blurb}</p>
            </button>
          );
        })}
      </div>
      <BottomCta
        onClick={canAdvance ? onContinue : undefined}
        disabled={!canAdvance}
        label={canAdvance ? "Continue" : "Pick at least 2"}
        testId="lv-setup-step-2-cta"
      />
    </motion.div>
  );
}

// ── STEP 2b — Image Picker (heart-favourite UI) ──────────────────
function ImagePickerStep({
  focusId, focusLabel, isLast, indexLabel, selected, onToggleImage, onContinue, pool: poolProp,
}) {
  const pool   = poolProp || FOCUS_IMAGES[focusId] || [];
  const remaining = Math.max(0, MIN_IMAGES_PER_CATEGORY - selected.length);
  const canAdvance = selected.length >= MIN_IMAGES_PER_CATEGORY;
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.55 }}
      className="absolute inset-0 flex flex-col px-4 pt-20 pb-28 overflow-hidden"
      data-testid={`lv-setup-step-2-images-${focusId}`}
    >
      <div className="text-center px-2 mb-3">
        <p className="text-amber-300/85 text-[10px] uppercase tracking-[0.4em] mb-1">{indexLabel}</p>
        <h2 className="text-white font-medium"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "30px", letterSpacing: "-0.012em" }}>
          {focusLabel}
        </h2>
        <p className="text-white/55 text-xs mt-1">
          {canAdvance
            ? `${selected.length} selected · keep going if it feels right`
            : `Select at least ${remaining} more`}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto px-1 pb-3"
           style={{ scrollbarWidth: "thin" }}>
        <div className="grid grid-cols-2 gap-2.5">
          {pool.map((url) => {
            const sel = selected.includes(url);
            return (
              <button
                key={url}
                onClick={() => onToggleImage(url)}
                data-testid={`lv-img-${focusId}-${pool.indexOf(url)}`}
                className="relative rounded-xl overflow-hidden transition-transform duration-300 active:scale-95"
                style={{
                  aspectRatio: "4/5",
                  border: `1px solid ${sel ? "rgba(217,165,90,0.65)" : "rgba(255,255,255,0.06)"}`,
                  boxShadow: sel ? "0 0 26px -4px rgba(217,165,90,0.55), inset 0 0 0 2px rgba(217,165,90,0.18)" : "none",
                }}
              >
                <img src={url} alt="" className="w-full h-full object-cover"
                     style={{ filter: sel ? "saturate(1.0) brightness(0.95)" : "saturate(0.85) brightness(0.78)" }} />
                <div aria-hidden className="absolute inset-0"
                     style={{ background: sel
                       ? "linear-gradient(180deg, transparent 60%, rgba(20,14,8,0.55))"
                       : "linear-gradient(180deg, transparent 50%, rgba(5,4,2,0.45))" }} />
                <div
                  className="absolute top-2 left-2 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300"
                  style={{
                    background: sel ? "rgba(217,165,90,0.80)" : "rgba(0,0,0,0.45)",
                    backdropFilter: "blur(8px)",
                    border: `1px solid ${sel ? "rgba(217,165,90,0.95)" : "rgba(255,255,255,0.18)"}`,
                  }}
                >
                  <Heart
                    className="w-4 h-4"
                    fill={sel ? "rgba(26,20,16,0.95)" : "transparent"}
                    color={sel ? "rgba(26,20,16,0.95)" : "rgba(255,255,255,0.85)"}
                    strokeWidth={1.8}
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <BottomCta
        onClick={canAdvance ? onContinue : undefined}
        disabled={!canAdvance}
        label={isLast ? (canAdvance ? "Continue" : `Select ${remaining} more`)
                       : (canAdvance ? "Next Category" : `Select ${remaining} more`)}
        testId="lv-setup-step-2-images-cta"
      />
    </motion.div>
  );
}

// ── STEP 3 — Word Confirmation ────────────────────────────────────
function IdentityReviewStep({ focuses, identity, onRemoveLine, onEnter }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.55 }}
      className="absolute inset-0 flex flex-col items-center justify-start px-6 pt-20 pb-32 max-w-xl mx-auto overflow-y-auto"
      data-testid="lv-setup-step-3"
    >
      <h2 className="text-white font-medium text-center mb-1"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "30px", letterSpacing: "-0.012em" }}>
        This is who you're stepping into
      </h2>
      <p className="text-white/45 text-xs uppercase tracking-[0.32em] mb-6">Identity</p>

      <div className="w-full space-y-4">
        {focuses.map((fid) => {
          const focus = FOCUS_OPTIONS.find((x) => x.id === fid);
          const lines = identity[fid] || [];
          return (
            <div key={fid} className="rounded-xl px-4 py-3.5"
                 style={{ background: "rgba(15,11,8,0.72)", border: "1px solid rgba(217,165,90,0.18)" }}>
              <p className="text-amber-300/85 text-[10px] uppercase tracking-[0.32em] mb-2">{focus?.label}</p>
              <div className="space-y-1">
                {lines.length === 0 && (
                  <p className="text-white/35 text-sm italic">No lines for this focus.</p>
                )}
                {lines.map((line) => (
                  <div key={line}
                       className="flex items-center justify-between gap-3 group">
                    <p className="text-white/85 text-base flex-1"
                       style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
                      "{line}"
                    </p>
                    <button
                      onClick={() => onRemoveLine(fid, line)}
                      data-testid={`lv-remove-line-${fid}-${lines.indexOf(line)}`}
                      className="text-white/25 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                      aria-label="Remove line"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <BottomCta onClick={onEnter} label="Enter My Vision" testId="lv-setup-step-3-cta" />
    </motion.div>
  );
}

// ── STEP 4 — Fade-to-Black Transition ────────────────────────────
function TransitionStep() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7, ease: "easeInOut" }}
      className="absolute inset-0 flex items-center justify-center bg-black z-30"
      data-testid="lv-setup-step-4"
    >
      <motion.div
        className="rounded-full"
        style={{
          width: 12, height: 12,
          background: "rgba(217,165,90,0.95)",
          boxShadow: "0 0 60px rgba(217,165,90,0.85)",
        }}
        animate={{ scale: [1, 2.6, 1], opacity: [0.85, 1, 0.65] }}
        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
      />
    </motion.div>
  );
}

// ── Reusable bottom CTA (with "Keep it simple." anchor) ───────────
function BottomCta({ onClick, label, testId, disabled }) {
  return (
    <div className="absolute bottom-0 inset-x-0 px-6 pb-7 pt-12 z-20"
         style={{ background: "linear-gradient(180deg, transparent, rgba(5,4,2,0.85) 55%, #050402)" }}>
      <div className="max-w-xl mx-auto flex flex-col items-center">
        <button
          onClick={onClick}
          disabled={disabled}
          data-testid={testId}
          className="w-full py-4 rounded-full text-white font-medium text-[14px] tracking-[0.06em] uppercase transition-all duration-300 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: "linear-gradient(135deg, rgba(217,165,90,0.95), rgba(180,120,60,0.95))",
            boxShadow: "0 14px 38px -12px rgba(217,165,90,0.65), inset 0 1px 0 rgba(255,255,255,0.10)",
          }}
        >
          <span className="inline-flex items-center gap-2 justify-center">
            {label} <ArrowRight className="w-4 h-4" />
          </span>
        </button>
        <p className="mt-3 text-white/35 text-[10px] uppercase tracking-[0.45em]" style={{ fontWeight: 500 }}>
          Keep it simple.
        </p>
      </div>
    </div>
  );
}
